import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Activity, RefreshCw, AlertTriangle, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * COLLABORATE → Activity. A chronological, workspace-scoped feed of
 * dashboard/report save activity — who changed what, when, and with
 * what save-note. Distinct from Discussions (human conversations):
 * this is an automatic record of what happened in the workspace.
 */
export default function ActivityPage() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [dashboardFilter, setDashboardFilter] = useState('');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 200 };
      if (dashboardFilter) params.dashboard_id = dashboardFilter;
      if (since) params.since = `${since}T00:00:00+00:00`;
      if (until) params.until = `${until}T23:59:59.999+00:00`;
      const body = await api.getWorkspaceActivity(params);
      setRows(Array.isArray(body?.data) ? body.data : []);
    } catch (e) {
      setError(String(e?.message || e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dashboardFilter, since, until]);

  useEffect(() => { load(); }, [load]);

  const dashboardOptions = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    const seen = new Map();
    rows.forEach(r => {
      if (r.dashboard_id && !seen.has(r.dashboard_id)) {
        seen.set(r.dashboard_id, r.dashboard_name || r.dashboard_id);
      }
    });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const clearFilters = () => {
    setDashboardFilter(''); setSince(''); setUntil(''); setQuery('');
  };
  const hasFilters = Boolean(dashboardFilter || since || until || query);

  const filtered = useMemo(() => {
    if (!Array.isArray(rows)) return rows;
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => {
      const hay = [r.dashboard_name, r.snapshot_name, r.note, r.saved_by].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query]);

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="activity-page">
      <div className="flex items-start gap-3 mb-5 pb-4 border-b border-border/40">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
          <Activity className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Workspace Activity</div>
          <h1 className="text-xl font-heading font-semibold text-foreground">Recent activity</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Every dashboard and report save across this workspace, newest first.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-muted-foreground/70 hover:text-primary disabled:opacity-40 shrink-0 mt-1 p-1.5 rounded-lg hover:bg-muted/40 transition-colors"
          title="Refresh"
          aria-label="Refresh activity feed"
          data-testid="activity-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-rose-500/40 bg-rose-500/5 p-3 text-xs text-rose-400 flex items-center gap-2" data-testid="activity-error">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {Array.isArray(rows) && (rows.length > 0 || dashboardFilter || since || until) && (
        <div className="mb-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="activity-filters">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground flex flex-col gap-1">
            Dashboard
            <select
              value={dashboardFilter}
              onChange={e => setDashboardFilter(e.target.value)}
              className="bg-muted/30 border border-border/40 rounded-lg px-2 py-1.5 text-xs text-foreground outline-none focus:border-primary/50 normal-case tracking-normal"
              data-testid="activity-filter-dashboard"
            >
              <option value="">All dashboards</option>
              {dashboardOptions.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </label>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground flex flex-col gap-1">
            From
            <input
              type="date"
              value={since}
              onChange={e => setSince(e.target.value)}
              className="bg-muted/30 border border-border/40 rounded-lg px-2 py-1.5 text-xs text-foreground outline-none focus:border-primary/50"
              data-testid="activity-filter-since"
            />
          </label>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground flex flex-col gap-1">
            To
            <input
              type="date"
              value={until}
              onChange={e => setUntil(e.target.value)}
              className="bg-muted/30 border border-border/40 rounded-lg px-2 py-1.5 text-xs text-foreground outline-none focus:border-primary/50"
              data-testid="activity-filter-until"
            />
          </label>
        </div>
      )}

      {hasFilters && (
        <div className="mb-2 flex justify-end">
          <button
            type="button"
            onClick={clearFilters}
            className="text-[10px] text-muted-foreground hover:text-primary underline underline-offset-2"
            data-testid="activity-clear-filters"
          >
            Clear filters
          </button>
        </div>
      )}

      {Array.isArray(rows) && rows.length > 0 && (
        <input
          type="search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Filter by dashboard, note, or author…"
          className="w-full mb-3 bg-muted/30 border border-border/40 rounded-lg px-3 py-2 text-xs text-foreground outline-none focus:border-primary/50"
          data-testid="activity-search"
        />
      )}

      {loading && !rows && (
        <div className="text-xs text-muted-foreground flex items-center gap-2" data-testid="activity-loading">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading activity…
        </div>
      )}

      {Array.isArray(filtered) && filtered.length === 0 && !loading && (
        <div className="text-xs text-muted-foreground border border-dashed border-border/40 rounded-xl p-8 text-center" data-testid="activity-empty">
          {query
            ? `No entries matching “${query}”`
            : rows?.length === 0
              ? 'No activity recorded yet in this workspace. Saves to dashboards and reports will appear here.'
              : 'No entries matching your filter.'}
        </div>
      )}

      {Array.isArray(filtered) && filtered.length > 0 && (
        <ul className="space-y-2" data-testid="activity-list">
          {filtered.map(r => (
            <li
              key={r.version_id}
              className="rounded-xl border border-border/40 bg-card/40 px-4 py-3 hover:border-border/70 transition-colors"
              data-testid={`activity-row-${r.version_id}`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <div className="text-sm font-medium text-foreground truncate">{r.dashboard_name}</div>
                <div className="text-[10px] text-muted-foreground font-mono shrink-0">
                  {r.saved_at ? new Date(r.saved_at).toLocaleString() : '—'}
                </div>
              </div>
              <div className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                <span className="font-mono">by {r.saved_by || 'unknown'}</span>
                <span>·</span>
                <span>{r.widget_count ?? 0} widgets at save</span>
                {r.snapshot_name && r.snapshot_name !== r.dashboard_name && (
                  <>
                    <span>·</span>
                    <span>was named “{r.snapshot_name}”</span>
                  </>
                )}
              </div>
              {r.note && (
                <div
                  className="mt-1.5 text-[11px] italic text-muted-foreground/90 line-clamp-3"
                  data-testid={`activity-note-${r.version_id}`}
                >
                  “{r.note}”
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {Array.isArray(rows) && rows.length > 0 && (
        <div className="mt-3 text-[10px] text-muted-foreground text-right" data-testid="activity-counts">
          {query ? `${filtered.length} of ${rows.length} matching` : `${rows.length} saves shown`}
        </div>
      )}
    </div>
  );
}
